import React, { useState, useEffect } from 'react';
import { FiClock, FiCheckCircle, FiSave } from 'react-icons/fi';
import api from '@/services/api';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const DAYS = [
  { key: 'monday', label: 'Segunda-feira' },
  { key: 'tuesday', label: 'Terça-feira' },
  { key: 'wednesday', label: 'Quarta-feira' },
  { key: 'thursday', label: 'Quinta-feira' },
  { key: 'friday', label: 'Sexta-feira' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' }
];

const DEFAULT_HOURS = {
  monday: { is_open: true, open_time: '08:00', close_time: '18:00' },
  tuesday: { is_open: true, open_time: '08:00', close_time: '18:00' },
  wednesday: { is_open: true, open_time: '08:00', close_time: '18:00' },
  thursday: { is_open: true, open_time: '08:00', close_time: '18:00' },
  friday: { is_open: true, open_time: '08:00', close_time: '18:00' },
  saturday: { is_open: true, open_time: '08:00', close_time: '14:00' },
  sunday: { is_open: false, open_time: '00:00', close_time: '00:00' }
};

const OperatingHoursCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadHours();
  }, []);

  const loadHours = async () => {
    try {
      const response = await api.get('/stores/my/operating-hours');
      if (response.data.operating_hours && Object.keys(response.data.operating_hours).length > 0) {
        setHours(response.data.operating_hours);
      }
      setIsOpen(response.data.is_open);
      setStatusMessage(response.data.status_message);
    } catch (error) {
      console.error('Error loading hours:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDayToggle = (day) => {
    setHours(prev => ({
      ...prev,
      [day]: { ...prev[day], is_open: !prev[day].is_open }
    }));
    setHasChanges(true);
  };

  const handleTimeChange = (day, field, value) => {
    setHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/stores/my/operating-hours', { operating_hours: hours });
      alert('Horário de funcionamento salvo!');
      setHasChanges(false);
      loadHours();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const applyToWeekdays = () => {
    const mondayHours = hours.monday;
    setHours(prev => ({
      ...prev,
      tuesday: { ...mondayHours },
      wednesday: { ...mondayHours },
      thursday: { ...mondayHours },
      friday: { ...mondayHours }
    }));
    setHasChanges(true);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FiClock className="text-2xl" />
            <div>
              <h3 className="font-semibold">Horário de Funcionamento</h3>
              <p className="text-sm text-purple-100">Defina quando sua loja está aberta</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            isOpen ? 'bg-green-500' : 'bg-red-500'
          }`}>
            {isOpen ? '🟢 Aberta' : '🔴 Fechada'}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Current Status */}
        <div className={`p-3 rounded-xl text-center ${isOpen ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
          {isOpen ? (
            <span>Sua loja está <strong>aberta</strong>. {statusMessage}</span>
          ) : (
            <span><strong>Loja fechada</strong>. {statusMessage}</span>
          )}
        </div>

        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-700">
          <strong>💡 Dica:</strong> Clientes podem comprar mesmo com a loja fechada. 
          O pedido será preparado assim que você abrir.
        </div>

        {/* Quick Action */}
        <button
          onClick={applyToWeekdays}
          className="text-sm text-purple-600 hover:text-purple-800 underline"
        >
          Aplicar horário de segunda para todos os dias úteis
        </button>

        {/* Days List */}
        <div className="space-y-2">
          {DAYS.map(({ key, label }) => (
            <div 
              key={key}
              className={`p-3 rounded-xl border transition-all ${
                hours[key]?.is_open 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleDayToggle(key)}
                    className={`w-10 h-6 rounded-full transition-colors relative ${
                      hours[key]?.is_open ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                      hours[key]?.is_open ? 'left-5' : 'left-1'
                    }`} />
                  </button>
                  <span className={`font-medium ${hours[key]?.is_open ? 'text-gray-800' : 'text-gray-400'}`}>
                    {label}
                  </span>
                </div>

                {hours[key]?.is_open && (
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={hours[key]?.open_time || '08:00'}
                      onChange={e => handleTimeChange(key, 'open_time', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                    />
                    <span className="text-gray-400">às</span>
                    <input
                      type="time"
                      value={hours[key]?.close_time || '18:00'}
                      onChange={e => handleTimeChange(key, 'close_time', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                    />
                  </div>
                )}

                {!hours[key]?.is_open && (
                  <span className="text-sm text-gray-400">Fechado</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
            hasChanges 
              ? 'bg-purple-600 text-white hover:bg-purple-700' 
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving ? (
            'Salvando...'
          ) : hasChanges ? (
            <>
              <FiSave /> Salvar Alterações
            </>
          ) : (
            <>
              <FiCheckCircle /> Salvo
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OperatingHoursCard;
